import { useState, type FormEvent } from "react";

import { sendMagicLink, signInWithProvider, AuthError } from "../lib/auth";

/**
 * Where to land after sign-in. The Invitation screen (and any future gated
 * deep-link) bounces unauthed users to `/login?next=<path>`; we thread that
 * through Better Auth's `callbackURL` so the magic-link / OAuth round-trip
 * resumes there instead of dropping the user on `/` (which, for a brand-new
 * invitee, routes into create-an-org and silently abandons the invite).
 *
 * Same-origin absolute paths only — reject protocol-relative (`//host`) and
 * absolute URLs to avoid an open redirect.
 */
function postLoginTarget(): string {
  const next = new URLSearchParams(window.location.search).get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

/**
 * Sign-in screen. Three options: Google, GitHub, or email magic-link.
 * No email+password — by design (see plan PR 4, locked decision #2).
 *
 * Lives OUTSIDE the `<RequireAuth>` guard (see App.tsx). The route stays
 * mounted at /login regardless of session state; on successful sign-in the
 * SPA hits "/" which is guarded and triggers the operator-load path.
 */
export function Login() {
  const [email, setEmail] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthBusy, setOauthBusy] = useState<"google" | "github" | null>(null);

  async function onGoogle() {
    setError(null);
    setOauthBusy("google");
    try {
      await signInWithProvider("google", postLoginTarget());
    } catch (e) {
      setError(e instanceof AuthError ? e.message : "Google sign-in failed");
      setOauthBusy(null);
    }
  }

  async function onGitHub() {
    setError(null);
    setOauthBusy("github");
    try {
      await signInWithProvider("github", postLoginTarget());
    } catch (e) {
      setError(e instanceof AuthError ? e.message : "GitHub sign-in failed");
      setOauthBusy(null);
    }
  }

  async function onEmail(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setError(null);
    setEmailSubmitting(true);
    try {
      await sendMagicLink(email, postLoginTarget());
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Failed to send magic link");
    } finally {
      setEmailSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          padding: 32,
          background: "var(--ad-surface-1)",
          color: "var(--ad-fg-1)",
          border: "1px solid var(--ad-hairline)",
          borderRadius: 12,
          boxShadow: "var(--ad-card-shadow)",
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 4 }}>
          AuthenSee Console
        </h1>
        <p style={{ fontSize: 13, color: "var(--ad-fg-3)", marginBottom: 24 }}>
          Sign in to set up AuthenSee for your apps.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            type="button"
            className="ad-btn ad-btn-ghost"
            onClick={onGoogle}
            disabled={oauthBusy !== null || emailSubmitting}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {oauthBusy === "google" ? "Redirecting…" : "Continue with Google"}
          </button>
          <button
            type="button"
            className="ad-btn ad-btn-ghost"
            onClick={onGitHub}
            disabled={oauthBusy !== null || emailSubmitting}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {oauthBusy === "github" ? "Redirecting…" : "Continue with GitHub"}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "20px 0 16px",
            fontSize: 11,
            color: "var(--ad-fg-3)",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          <div style={{ flex: 1, height: 1, background: "var(--ad-hairline)" }} />
          or
          <div style={{ flex: 1, height: 1, background: "var(--ad-hairline)" }} />
        </div>

        {emailSent ? (
          <div
            style={{
              padding: 12,
              border: "1px solid var(--ad-hairline)",
              borderRadius: 8,
              fontSize: 13,
              background: "var(--ad-surface-2)",
            }}
          >
            Check your inbox at <strong>{email}</strong> for a sign-in link. It expires in 5 minutes.
            <button
              type="button"
              onClick={() => {
                setEmailSent(false);
                setEmail("");
              }}
              style={{
                marginTop: 8,
                background: "transparent",
                border: 0,
                padding: 0,
                color: "var(--rb-orange)",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={onEmail} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={emailSubmitting || oauthBusy !== null}
              style={{
                padding: "10px 12px",
                border: "1px solid var(--ad-hairline)",
                borderRadius: 8,
                fontSize: 14,
              }}
            />
            <button
              type="submit"
              className="ad-btn ad-btn-primary"
              disabled={emailSubmitting || oauthBusy !== null || !email}
              style={{ width: "100%", justifyContent: "center" }}
            >
              {emailSubmitting ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
        )}

        {error && (
          <div
            style={{
              marginTop: 16,
              padding: 10,
              borderRadius: 8,
              fontSize: 13,
              background: "color-mix(in oklab, var(--ad-error) 10%, transparent)",
              color: "var(--ad-error)",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
